import { type Domain, DOMAINS } from '~/features/navigation/domains';

export const USER_ROLES = {
  AdminAbbott: 'abbott_admin',
  AbbottUser: 'abbott_user',
  HcpAdmin: 'hcp_admin',
  HcpUser: 'hcp',
  NonHcpAdmin: 'admin',
} as const;

export type UserRoleValue = (typeof USER_ROLES)[keyof typeof USER_ROLES];

export const USER_ROLE_NAME: Record<UserRoleValue, string> = {
  [USER_ROLES.AdminAbbott]: 'Admin Abbott',
  [USER_ROLES.AbbottUser]: 'Abbott User',
  [USER_ROLES.HcpAdmin]: 'HCP Admin',
  [USER_ROLES.HcpUser]: 'HCP',
  [USER_ROLES.NonHcpAdmin]: 'Non-HCP Admin',
};

export const ALLOWED_ROLE_CREATION: Record<UserRoleValue, UserRoleValue[]> = {
  [USER_ROLES.AdminAbbott]: [
    USER_ROLES.AdminAbbott,
    USER_ROLES.AbbottUser,
    USER_ROLES.NonHcpAdmin,
    USER_ROLES.HcpAdmin,
  ],
  [USER_ROLES.AbbottUser]: [USER_ROLES.NonHcpAdmin, USER_ROLES.HcpAdmin],
  [USER_ROLES.NonHcpAdmin]: [
    USER_ROLES.NonHcpAdmin,
    USER_ROLES.HcpUser,
    USER_ROLES.HcpAdmin,
  ],
  [USER_ROLES.HcpAdmin]: [
    USER_ROLES.NonHcpAdmin,
    USER_ROLES.HcpUser,
    USER_ROLES.HcpAdmin,
  ],
  [USER_ROLES.HcpUser]: [],
};

export const ALLOWED_ROLE_CREATION_BY_DOMAIN: Record<Domain, UserRoleValue[]> =
  {
    [DOMAINS.ABBOTT_ADMINISTRATION]: [
      USER_ROLES.AdminAbbott,
      USER_ROLES.AbbottUser,
    ],
    [DOMAINS.CUSTOMER_ADMINISTRATION]: [
      USER_ROLES.NonHcpAdmin,
      USER_ROLES.HcpAdmin,
      USER_ROLES.HcpUser,
    ],
    [DOMAINS.USER_ADMINISTRATION]: [
      USER_ROLES.NonHcpAdmin,
      USER_ROLES.HcpAdmin,
      USER_ROLES.HcpUser,
    ],
    [DOMAINS.EDUCATION]: [],
    [DOMAINS.OUTPATIENT]: [],
  };

// Defines which roles can be edited by each role within each domain.
// Based on https://abbott-my.sharepoint.com/:x:/r/personal/safia_hossain_abbott_com/_layouts/15/Doc.aspx?sourcedoc=%7B529BD92C-D22C-411D-A5A1-8D71E7A6EEA6%7D&file=v3%20CLM%20Web%20Domain%20Matrix.xlsx&action=default&mobileredirect=true
export const ALLOWED_ROLE_EDIT: Record<
  Domain,
  Partial<Record<UserRoleValue, UserRoleValue[]>>
> = {
  [DOMAINS.ABBOTT_ADMINISTRATION]: {
    [USER_ROLES.AdminAbbott]: [USER_ROLES.AdminAbbott, USER_ROLES.AbbottUser],
    [USER_ROLES.AbbottUser]: [USER_ROLES.AbbottUser],
    [USER_ROLES.HcpAdmin]: [],
    [USER_ROLES.NonHcpAdmin]: [],
    [USER_ROLES.HcpUser]: [],
  },
  [DOMAINS.CUSTOMER_ADMINISTRATION]: {
    [USER_ROLES.AdminAbbott]: [USER_ROLES.HcpAdmin, USER_ROLES.NonHcpAdmin],
    [USER_ROLES.AbbottUser]: [USER_ROLES.HcpAdmin, USER_ROLES.NonHcpAdmin],
    [USER_ROLES.HcpAdmin]: [],
    [USER_ROLES.NonHcpAdmin]: [],
    [USER_ROLES.HcpUser]: [],
  },
  [DOMAINS.USER_ADMINISTRATION]: {
    [USER_ROLES.AdminAbbott]: [],
    [USER_ROLES.AbbottUser]: [],
    [USER_ROLES.HcpAdmin]: [
      USER_ROLES.HcpUser,
      USER_ROLES.NonHcpAdmin,
      USER_ROLES.HcpAdmin,
    ],
    [USER_ROLES.NonHcpAdmin]: [
      USER_ROLES.NonHcpAdmin,
      USER_ROLES.HcpUser,
      USER_ROLES.HcpAdmin,
    ],
    [USER_ROLES.HcpUser]: [],
  },
  [DOMAINS.OUTPATIENT]: {
    [USER_ROLES.AdminAbbott]: [],
    [USER_ROLES.AbbottUser]: [],
    [USER_ROLES.HcpAdmin]: [],
    [USER_ROLES.NonHcpAdmin]: [],
    [USER_ROLES.HcpUser]: [],
  },
  [DOMAINS.EDUCATION]: {
    [USER_ROLES.AdminAbbott]: [],
    [USER_ROLES.AbbottUser]: [],
    [USER_ROLES.HcpAdmin]: [],
    [USER_ROLES.NonHcpAdmin]: [],
    [USER_ROLES.HcpUser]: [],
  },
};
